import React from "react";
import type { ApiDilemma } from "../types";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/utils/date";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import type { RowSelectionState } from "@tanstack/react-table";
import MobileListWrapper from "@/components/mobile-list-wrapper";
import { statusBadgeVariant } from "../utils";
import { DILEMMAS_STATUS } from "../constants";

interface ListContentProps {
  data: ApiDilemma[];
  isLoading: boolean;
  className?: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  emptyListComponent: React.ReactElement;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  context?: "submissions";
}

const ListContent = ({
  data,
  isLoading,
  className,
  page,
  pageCount,
  onPageChange,
  emptyListComponent,
  rowSelection,
  onRowSelectionChange,
  context,
}: ListContentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const statusLabel = (status: string): string => {
    if (context === "submissions" && status === DILEMMAS_STATUS.DRAFT) {
      return t("modules.dilemmas.keys.status_returned");
    }
    switch (status) {
      case DILEMMAS_STATUS.DRAFT:
        return t("modules.dilemmas.keys.status_draft");
      case DILEMMAS_STATUS.SCHEDULED:
        return t("modules.dilemmas.keys.status_scheduled");
      case DILEMMAS_STATUS.PUBLISHED:
        return t("modules.dilemmas.keys.status_published");
      case DILEMMAS_STATUS.ARCHIVED:
        return t("modules.dilemmas.keys.status_archived");
      case DILEMMAS_STATUS.SUBMITTED:
        return t("modules.dilemmas.keys.status_submitted");
      default:
        return status;
    }
  };

  return (
    <MobileListWrapper
      data={data}
      isLoading={isLoading}
      className={className}
      page={page}
      pageCount={pageCount}
      onPageChange={onPageChange}
      emptyListComponent={emptyListComponent}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      onItemClick={(dilemma) => navigate(ROUTES.DILEMMA_DETAILS(dilemma.id))}
      renderItem={(dilemma) => (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{dilemma.name}</h3>
            <Badge variant={statusBadgeVariant(dilemma.status)}>
              {statusLabel(dilemma.status)}
            </Badge>
          </div>
          <div className="flex items-center my-1 justify-between">
            <span className="text-sm" style={{ color: dilemma.topic.color }}>
              {dilemma.topic.name}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-gray-500">
              {dilemma.topic.reference}
            </span>
            <p className="text-sm text-gray-500">
              {formatDate(dilemma.created_at)}
            </p>
          </div>
        </>
      )}
    />
  );
};

export default ListContent;
